import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Mail, MapPin, CheckCircle2, Gauge, Eye, Target, ShieldCheck,
  Quote, Send, Loader2, AlertCircle, Layers, Sparkles, BookOpen, FileCheck2,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { Eyebrow } from "../components/Eyebrow";
import { Button } from "../components/ui/button";
import { PARTNERS, COMPANY_ADDRESS } from "../mock";
import { LogoImage } from "../components/LogoImage";
import { PRODUCTS_DATA } from "../data/productsData";
import { submitLead, emptyLeadForm } from "../lib/api";

const ACCENTS = [
  "from-[#15348C] to-[#0A1F5C]",
  "from-[#6BBE32] to-[#4F9622]",
  "from-[#8BD835] to-[#6BBE32]",
  "from-[#4F9622] to-[#15348C]",
];

const HIGHLIGHT_ICONS = [Gauge, Eye, Target, ShieldCheck];
const EXPLORE_ICONS = [Layers, Sparkles, BookOpen, FileCheck2];

const CHECKLIST = [
  "Identity Verified",
  "Environment Scan",
  "AI Monitoring",
  "Assessment Active",
];

const SKILL_BARS = [
  { label: "Problem Solving", value: 90 },
  { label: "System Design", value: 85 },
  { label: "Communication", value: 83 },
  { label: "Technical Knowledge", value: 88 },
];

const INTEREST_OPTIONS = [
  "AI-Powered Assessments",
  "Enterprise Learning",
  "Corporate Training",
  "Hire-Train-Deploy",
  "AWS Cloud Labs",
  "AI-Powered Coding",
  "Other",
];

const AIPoweredAssessmentsPage = () => {
  const data = PRODUCTS_DATA["ai-powered-assessments"];
  const {
    eyebrow, title, heroSubheadline, subtitle, highlights, stats,
    differenceEyebrow, differenceHeading, differenceCopy, differenceCtaLabel, differenceCtaHref,
    capabilitiesHeading, capabilitiesIntro, capabilities,
    howHeading, howSteps,
    whoEyebrow, whoHeading, whoItems,
    testimonial,
    ctaHeadline, ctaSupporting,
    exploreHeading, exploreCards,
  } = data;

  const [activeUseCase, setActiveUseCase] = useState(0);

  const [form, setForm] = useState({ ...emptyLeadForm(), interest: "AI-Powered Assessments" });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", message: "" });
    try {
      await submitLead({ ...form, source: "ai-powered-assessments-page", formType: "product-enquiry" });
      setStatus({ state: "success", message: "Thanks! Our enterprise team will reach out within one working day." });
      setForm({ ...emptyLeadForm(), interest: "AI-Powered Assessments" });
      setTimeout(() => setStatus({ state: "idle", message: "" }), 6000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({ state: "error", message: typeof detail === "string" ? detail : "Something went wrong. Please try again." });
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <Header />

      {/* 1) HERO — split layout */}
      <section className="bg-gradient-to-br from-[#15348C] to-[#0A1F5C] text-white relative overflow-hidden">
        <div
          className="sg-grid-drift absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize: "22px 22px" }}
        />
        <div className="sg-float-a pointer-events-none absolute -top-24 right-0 w-96 h-96 bg-[#6BBE32]/15 rounded-full blur-3xl" />
        <div className="sg-float-b pointer-events-none absolute -bottom-24 -left-10 w-80 h-80 bg-[#8BD835]/15 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 py-20 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal variant="left">
            <div className="flex"><Eyebrow dark>{eyebrow}</Eyebrow></div>
            <h1 className="text-4xl lg:text-5xl font-bold leading-tight">
              {title.split(" ").slice(0, 1).join(" ")}{" "}
              <span className="text-[#8BD835]">{title.split(" ").slice(1).join(" ")}</span>
            </h1>
            {heroSubheadline && <p className="mt-3 text-xl font-semibold text-gray-100">{heroSubheadline}</p>}
            <p className="mt-4 text-gray-300 leading-relaxed max-w-xl">{subtitle}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact">
                <Button className="sg-btn-glow bg-[#6BBE32] hover:bg-[#4F9622] text-white font-semibold px-6 h-12 rounded-md shadow-lg shadow-[#6BBE32]/30">
                  Talk to Our Experts <ArrowRight size={17} className="ml-1" />
                </Button>
              </Link>
              <Link to="/offerings/all">
                <Button className="bg-transparent border-2 border-white/40 hover:bg-white/10 hover:border-white text-white font-semibold px-6 h-12 rounded-md">
                  See All Products
                </Button>
              </Link>
            </div>
            {highlights && highlights.length > 0 && (
              <div className="mt-9 flex flex-wrap gap-x-8 gap-y-3">
                {highlights.map((h, i) => {
                  const Icon = HIGHLIGHT_ICONS[i % HIGHLIGHT_ICONS.length];
                  return (
                    <div key={h} className="flex items-center gap-2 text-sm text-gray-300">
                      <Icon size={16} className="text-[#8BD835]" /> {h}
                    </div>
                  );
                })}
              </div>
            )}
          </Reveal>

          {/* Illustrative "assessment in progress" UI card — icons only, no photography */}
          <Reveal variant="right" delay={120}>
            <div className="relative">
              <div className="pointer-events-none absolute -inset-4 bg-gradient-to-br from-[#6BBE32]/20 to-[#8BD835]/10 rounded-3xl blur-2xl" />
              <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden max-w-sm ml-auto">
                <div className="flex items-center gap-1.5 px-4 py-3 bg-[#F8F9FA] border-b border-gray-100">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                  <span className="ml-3 text-xs text-gray-400 font-medium">Assessment in Progress</span>
                </div>
                <div className="p-6 space-y-3">
                  {CHECKLIST.map((c, i) => (
                    <div key={c} className="sg-chip flex items-center gap-3" style={{ animationDelay: `${i * 120}ms` }}>
                      <div className="w-6 h-6 rounded-full bg-[#8BD835]/15 flex items-center justify-center shrink-0">
                        <CheckCircle2 size={14} className="text-[#4F9622]" />
                      </div>
                      <span className="text-sm font-medium text-[#15348C]">{c}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="sg-chip absolute -top-5 -left-5 bg-white rounded-xl shadow-xl px-4 py-2 text-xs font-bold text-[#15348C] border border-gray-100" style={{ animationDelay: "480ms" }}>
                Real Skills · Real Talent · Real Outcomes
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 2) STATS STRIP */}
      {stats && stats.length > 0 && (
        <section className="py-4 relative z-10 -mt-8">
          <div className="max-w-6xl mx-auto px-4">
            <Reveal variant="scale">
              <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-100 rounded-2xl border border-gray-100 bg-white shadow-xl shadow-[#15348C]/10">
                {stats.map((s) => (
                  <div key={s.label} className="p-6 text-center group">
                    <div className="text-3xl lg:text-4xl font-bold text-[#15348C] group-hover:text-[#6BBE32] transition-colors">{s.value}</div>
                    <div className="text-sm text-gray-500 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* 3) THE DIFFERENCE */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal variant="left">
            <Eyebrow color="#6BBE32">{differenceEyebrow}</Eyebrow>
            <h2 className="text-[#15348C] leading-tight">{differenceHeading}</h2>
            <p className="mt-4 text-gray-600 leading-relaxed">{differenceCopy}</p>
            {differenceCtaLabel && (
              <Link to={differenceCtaHref || "/contact"} className="mt-5 inline-flex items-center gap-1.5 text-[#15348C] font-semibold hover:text-[#6BBE32] transition-colors">
                {differenceCtaLabel} <ArrowRight size={16} />
              </Link>
            )}
          </Reveal>
          <Reveal variant="right" delay={100}>
            <div className="relative">
              <div className="sg-float-c pointer-events-none absolute -inset-6 bg-gradient-to-br from-[#15348C]/10 to-[#6BBE32]/10 rounded-3xl blur-2xl" />
              <div className="relative bg-gradient-to-br from-[#F8F9FA] to-[#EFF3E8] border border-gray-100 rounded-2xl p-10 flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#6BBE32] to-[#4F9622] flex items-center justify-center shadow-lg shadow-[#6BBE32]/30 mb-5">
                  <Target size={26} className="text-white" />
                </div>
                <div className="relative inline-block">
                  <span className="text-[#15348C] text-xl font-semibold">From potential to performance.</span>
                  <span className="block h-0.5 w-full bg-[#8BD835] mt-1 rounded-full" />
                </div>
                <p className="mt-4 text-gray-500 text-sm max-w-xs">Every assessment turns a candidate's potential into evidence you can act on.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 4) CAPABILITIES — sample results illustration */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal variant="left">
            <div className="relative">
              <div className="pointer-events-none absolute -inset-4 bg-gradient-to-br from-[#15348C]/10 to-[#6BBE32]/10 rounded-3xl blur-2xl" />
              <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="flex items-center gap-1.5 px-4 py-3 bg-[#F8F9FA] border-b border-gray-100">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                  <span className="ml-3 text-xs text-gray-400 font-medium">Sample Assessment Result</span>
                </div>
                <div className="p-6">
                  <div className="text-xs font-bold tracking-widest text-gray-400 mb-4">SKILL BREAKDOWN</div>
                  <div className="space-y-3">
                    {SKILL_BARS.map((b) => (
                      <div key={b.label}>
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>{b.label}</span>
                          <span className="font-semibold text-[#15348C]">{b.value}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-[#6BBE32] to-[#8BD835]" style={{ width: `${b.value}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal variant="right" delay={100}>
            <Eyebrow color="#4F9622">CAPABILITIES</Eyebrow>
            <h2 className="text-[#15348C] leading-tight">{capabilitiesHeading}</h2>
            {capabilitiesIntro && <p className="mt-3 text-gray-600 leading-relaxed">{capabilitiesIntro}</p>}
            <div className="mt-6 space-y-4">
              {capabilities.map((c) => {
                const Icon = c.icon;
                return (
                  <div key={c.title} className="flex gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#15348C]/10 flex items-center justify-center shrink-0">
                      <Icon size={17} className="text-[#15348C]" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#15348C] text-sm">{c.title}</div>
                      <p className="text-sm text-gray-600">{c.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <Link to="/solution/proctored-assessments" className="mt-6 inline-flex items-center gap-1.5 text-[#15348C] font-semibold hover:text-[#6BBE32] transition-colors">
              Explore All Capabilities <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* 5) USE CASES — interactive tabs */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="max-w-2xl mb-8">
              <Eyebrow color="#6BBE32">{whoEyebrow}</Eyebrow>
              <h2 className="text-[#15348C] leading-tight">{whoHeading}</h2>
            </div>
          </Reveal>
          <Reveal delay={60}>
            <div className="flex flex-wrap gap-2 border-b border-gray-100 mb-8">
              {whoItems.map((w, i) => (
                <button
                  key={w.title}
                  onClick={() => setActiveUseCase(i)}
                  className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                    activeUseCase === i ? "text-[#15348C] border-[#6BBE32]" : "text-gray-400 border-transparent hover:text-[#15348C]"
                  }`}
                >
                  {w.title}
                </button>
              ))}
            </div>
          </Reveal>
          <Reveal key={whoItems[activeUseCase].title} delay={0}>
            <div className="bg-[#F8F9FA] border border-gray-100 rounded-2xl p-8 flex flex-wrap items-center justify-between gap-4">
              <p className="text-lg text-gray-700 max-w-2xl">{whoItems[activeUseCase].desc}</p>
              <Link to="/contact" className="inline-flex items-center gap-1.5 text-[#15348C] font-semibold hover:text-[#6BBE32] transition-colors shrink-0">
                Learn More <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6) HOW IT WORKS — stepper */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="max-w-2xl mb-14">
              <Eyebrow color="#4F9622">HOW IT WORKS</Eyebrow>
              <h2 className="text-[#15348C] leading-tight">{howHeading}</h2>
            </div>
          </Reveal>
          <div className="flex flex-col lg:flex-row items-stretch lg:items-start gap-6 lg:gap-0">
            {howSteps.map((step, i) => (
              <React.Fragment key={step.title}>
                <Reveal variant="up" delay={i * 100} className="flex-1">
                  <div className="text-center px-2">
                    <div
                      className="mx-auto w-12 h-12 rounded-full text-white font-bold flex items-center justify-center shadow-lg"
                      style={{ background: ["#15348C", "#6BBE32", "#4F9622", "#0A1F5C"][i % 4] }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <h3 className="mt-4 text-[#15348C]" style={{ fontSize: "1.05rem" }}>{step.title}</h3>
                    <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">{step.desc}</p>
                  </div>
                </Reveal>
                {i < howSteps.length - 1 && (
                  <div className="hidden lg:flex items-center justify-center pt-6 text-gray-300">
                    <ArrowRight size={20} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* 7) TESTIMONIAL */}
      {testimonial && testimonial.quote && (
        <section className="py-20 bg-gradient-to-br from-[#15348C] to-[#0A1F5C] text-white relative overflow-hidden">
          <div className="sg-float-a pointer-events-none absolute -top-20 right-10 w-72 h-72 bg-[#6BBE32]/15 rounded-full blur-3xl" />
          <div className="sg-float-b pointer-events-none absolute -bottom-20 left-10 w-72 h-72 bg-[#8BD835]/15 rounded-full blur-3xl" />
          <div className="relative max-w-3xl mx-auto px-4 text-center">
            <Reveal>
              <Quote size={36} className="text-[#8BD835] mx-auto mb-6" />
              <p className="text-xl lg:text-2xl leading-relaxed font-medium">&ldquo;{testimonial.quote}&rdquo;</p>
              {testimonial.attribution && <p className="mt-6 text-gray-300 font-semibold">{testimonial.attribution}</p>}
            </Reveal>
          </div>
        </section>
      )}

      {/* 8) TRUSTED BY */}
      <section className="py-14 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div
              className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6 opacity-80"
              style={{ maskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)", WebkitMaskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)" }}
            >
              {PARTNERS.slice(0, 10).map((p) => (
                <LogoImage key={p.name} src={p.logo} alt={p.name}
                  className="h-8 max-w-[120px] object-contain"
                  fallbackClassName="text-[#15348C] font-bold text-sm" />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 9) OTHER SKILLSGAIN PRODUCTS */}
      {exploreCards && exploreCards.length > 0 && (
        <section className="py-20 bg-[#F8F9FA]">
          <div className="max-w-7xl mx-auto px-4">
            <Reveal>
              <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
                <div>
                  <Eyebrow color="#6BBE32">EXPLORE MORE</Eyebrow>
                  <h2 className="text-[#15348C] leading-tight">{exploreHeading}</h2>
                </div>
                <Link to="/offerings/all" className="hidden sm:inline-flex items-center gap-1.5 text-[#15348C] font-semibold hover:text-[#6BBE32] transition-colors">
                  View All Products <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {exploreCards.map((c, i) => {
                const Icon = EXPLORE_ICONS[i % EXPLORE_ICONS.length];
                const accent = ACCENTS[i % ACCENTS.length];
                return (
                  <Reveal key={c.title} variant="up" delay={i * 90}>
                    <Link to={c.href} className="group relative flex flex-col bg-white border border-gray-100 rounded-2xl overflow-hidden h-full hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                      <div className={`h-20 flex items-center justify-center bg-gradient-to-br ${accent}`}>
                        <Icon size={26} className="text-white" />
                      </div>
                      <div className="p-5 flex flex-col flex-1">
                        <h3 className="text-[#15348C] group-hover:text-[#6BBE32] transition-colors" style={{ fontSize: "1rem" }}>{c.title}</h3>
                        <p className="mt-1.5 text-gray-600 text-sm leading-relaxed flex-1">{c.desc}</p>
                        <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#15348C] group-hover:text-[#6BBE32] transition-colors">
                          Learn More <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* 10) FINAL CTA + LEAD FORM */}
      <section className="py-20 bg-gradient-to-br from-[#15348C] to-[#0A1F5C] text-white relative overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize: "20px 20px" }}
        />
        <div className="sg-float-a pointer-events-none absolute -top-24 -right-24 w-80 h-80 bg-[#6BBE32]/20 rounded-full blur-3xl" />
        <div className="sg-float-c pointer-events-none absolute -bottom-24 -left-24 w-80 h-80 bg-[#8BD835]/20 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-10">
          <Reveal variant="left">
            <Eyebrow dark>TALK TO OUR EXPERTS</Eyebrow>
            <h2 className="text-white leading-tight">{ctaHeadline}</h2>
            {ctaSupporting && <p className="mt-4 text-gray-300 leading-relaxed max-w-md">{ctaSupporting}</p>}
            <div className="mt-8 space-y-3">
              <a href="mailto:info@skillsgain.com" className="flex items-center gap-2 text-gray-200 hover:text-[#8BD835] transition-colors">
                <Mail size={16} /> info@skillsgain.com
              </a>
              <div className="flex items-start gap-2 text-gray-300">
                <MapPin size={16} className="mt-0.5 shrink-0" />
                <span>{COMPANY_ADDRESS.line1}, {COMPANY_ADDRESS.line2}<br />{COMPANY_ADDRESS.line3}, {COMPANY_ADDRESS.line4}</span>
              </div>
            </div>
          </Reveal>

          <Reveal variant="right" delay={100}>
            <div className="bg-white rounded-2xl p-6 lg:p-7 text-left">
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="assessments-lead-form">
                <LeadField label="Full Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                <LeadField label="Work Email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                <LeadField label="Company" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} />
                <LeadField label="Phone" type="tel" value={form.mobile_no} onChange={(v) => setForm({ ...form, mobile_no: v })} />
                <div className="sm:col-span-2">
                  <label className="text-sm font-semibold text-[#15348C]">Area of Interest</label>
                  <select
                    value={form.interest}
                    onChange={(e) => setForm({ ...form, interest: e.target.value })}
                    className="mt-1 w-full bg-white border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#6BBE32] transition-colors text-sm"
                  >
                    {INTEREST_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm font-semibold text-[#15348C]">Message<span className="text-[#4F9622]">*</span></label>
                  <textarea
                    required rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Tell us about your requirements…"
                    className="mt-1 w-full bg-white border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#6BBE32] transition-colors text-sm"
                  />
                </div>
                <div className="sm:col-span-2 flex items-center gap-4 flex-wrap">
                  <Button type="submit" disabled={status.state === "loading"}
                    className="sg-btn-glow bg-[#15348C] hover:bg-[#0A1F5C] text-white px-6 h-11 rounded-md disabled:opacity-60">
                    {status.state === "loading"
                      ? (<><Loader2 size={16} className="mr-2 animate-spin" /> Sending…</>)
                      : (<><Send size={16} className="mr-2" /> Talk to Our Experts</>)}
                  </Button>
                  {status.state === "success" && (
                    <span className="text-[#4F9622] text-sm flex items-center gap-1"><CheckCircle2 size={16} /> {status.message}</span>
                  )}
                  {status.state === "error" && (
                    <span className="text-red-600 text-sm flex items-center gap-1"><AlertCircle size={16} /> {status.message}</span>
                  )}
                </div>
                <p className="sm:col-span-2 text-xs text-gray-400">
                  By submitting this form, you agree to be contacted by Skillsgain regarding enterprise learning and skill development programs.
                </p>
              </form>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const LeadField = ({ label, value, onChange, type = "text", required }) => (
  <div>
    <label className="text-sm font-semibold text-[#15348C]">{label}{required && <span className="text-[#4F9622]">*</span>}</label>
    <input
      type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full bg-white border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#6BBE32] transition-colors text-sm"
    />
  </div>
);

export default AIPoweredAssessmentsPage;
